import { useMemo } from 'react'

import { useTranslation } from 'react-i18next'

import { Box, Flex, Text, TextButton } from '@enechain/ecloud-designsystem'

import { TakusoLastUpdated } from '~/components/balance/TakusoLastUpdated'
import { CompactAlert } from '~/components/common/CompactAlert'
import { RegionLoading } from '~/components/common/RegionLoading'
import { WeatherDataSource } from '~/components/common/WeatherDataSource'
import { WorkflowPageLayout } from '~/components/common/WorkflowPageLayout'
import { useBusinessAreaFilter } from '~/hooks/useBusinessAreaFilter'
import {
  BALANCE_DEMAND_LAYOUT_STORAGE_KEY,
  useGridCardLayout,
} from '~/hooks/useGridCardLayout'
import { useCurrentWorkflowCase } from '~/hooks/workflowCase/useCurrentWorkflowCase'
import { hasAwaitingKoma } from '~/models/awaitingVoltage'
import { FilterCard } from '~/pages/BalanceDemand/components/FilterCard'
import {
  AreaCodeGrid,
  areaCodeStorageKey,
  selectVisibleAreaCodes,
} from '~/pages/BalanceDemand/components/RegionGrid'
import {
  ANALYSIS_SERIES_KEYS,
  DEMAND_AREA_CODES,
  DETAIL_SERIES_KEYS,
  VOLTAGE_SERIES_KEYS,
} from '~/pages/BalanceDemand/constants'
import {
  convertDashboardResponse,
  useDemandDashboard,
} from '~/pages/BalanceDemand/hooks/useDemandDashboard'
import { useFilterState } from '~/pages/BalanceDemand/hooks/useFilterState'

// doji-grid-layout: design bg-data.js の GridStack 設定 (column:12, cellHeight:70,
// margin:6) と揃える。
//
// 高さの根拠 (RegionChart の実コードから積んだ概算):
//   ヘッダー行: padding 3+3=6px + テキスト(sm=12px)行 ≒ 16px → 計 22px
//   MAX/MINピル行: padding 3+3=6px + DemandPeakPill 1 行ぶん ≒ 16px → 計 22px
//     (summary != null 時のみ描画。既定は低圧未着でない実績が 1 コマでもあれば出る)
//   天気帯: minHeight 20px + padding 2+2=4px = 24px (weather トグル既定 ON)
//   軸ラベル行: padding-top 1px + lineHeight:1 (xs=10px) = 11px
//   チャート本体: minHeight 100px (コード内コメントの実測下限。これを下回ると
//     X 軸ラベルがカード下端で見切れる)
//   → 概算合計 ≒ 22+22+24+11+100 = 179px。design bg.html 実測で確認された
//     #4717 の 220px (カード ~198px 相当 + ピル行 ~22px) の方が保守的な実測値
//     なので、そちらを正の下限として引き続き採用する。
// 既定サイズ h=4 (298px = 4*70+3*6) は #4717 の 220px に余裕を持たせつつ、
// MIN h=3 (222px、220px を上回る最小のグリッド単位) までの縮小方向リサイズ
// 余地を確保する (旧実装は既定=下限=h=3 で下方向に縮められなかった)。
// 幅方向 (minW=3 列 ≒ 280〜300px 程度、コンテナ幅 1200px 前提の概算) は
// Y_AXIS_WIDTH=84px + 天気帯余白を確保してなお十分な余裕があるため据え置く。
const DEFAULT_CARD_SIZE = { w: 4, h: 4 }
const MIN_CARD_SIZE = { w: 3, h: 3 }
// export = R6: F5 test (index.test.tsx) がレイアウトリセットの localStorage
// key を参照するため、生文字列で二重定義しない。キー定数自体の SoT は
// `~/hooks/useGridCardLayout` 側 (= ログアウト時の破棄 `clearGridCardLayouts`
// と同じ定数を共有する必要があるため)。
export const LAYOUT_STORAGE_KEY = BALANCE_DEMAND_LAYOUT_STORAGE_KEY

export const BalanceDemandPage = (): React.ReactElement => {
  const { t } = useTranslation()

  // issue #7535 / conventions.md「業務エリアフィルタの原則」: エリアセレクターの
  // 選択肢 = 対象エリア (DEMAND_AREA_CODES、全 10 エリア) ∩ 業務エリア設定。
  // 取得・フォールバック・エラー公開のポリシーは共有 hook 側の JSDoc 参照
  // (`~/hooks/useBusinessAreaFilter`。 review 指摘: 発電側と複製すると画面間で
  // 業務エリア設定への追随が乖離するため統合した)。
  const { selectableAreaCodes, isBusinessAreasError } =
    useBusinessAreaFilter(DEMAND_AREA_CODES)

  const filter = useFilterState(selectableAreaCodes)
  const { data, isRegionLoading, isError } = useDemandDashboard(
    filter.displayDate,
  )

  const dashboard = data ? convertDashboardResponse(data) : undefined

  /**
   * **バナーは取得失敗のたびに出す**。 AC-BDB-013 は「IF 需要データの取得に
   * 失敗した場合」 で初回に限定していない。 5 秒間隔の背景 refetch が失敗した
   * ケースでも、 ユーザーには「今見ている値が古い」 ことを伝える必要がある
   * (2026-09-07 レビュー指摘)。
   *
   * 業務エリア取得の失敗もここに合流させる (issue #7535 repair)。 選択肢は
   * 失敗時も全 10 エリアのフォールバックを維持する (`useBusinessAreaFilter`
   * の why not 参照) ため、専用の空状態には落ちない — このバナーだけが
   * 「絞り込みが効いていない暫定表示」であることを伝える手段になる。
   */
  const showLoadError = isError || isBusinessAreasError

  /**
   * ローディングを止めるのは**初回取得が失敗した時だけ**。 成功後の背景 refetch
   * が失敗しても、 表示済みのカードは残す (バナーだけ足す)。 消してしまうと
   * 「一瞬だけ通信が切れた」 で画面の中身が飛ぶ。
   */
  const initialLoadFailed = data === undefined && isError

  // 親トグル (電圧階級別 / 分析補助 / 詳細) を子の実効可視性へ畳み込む
  // (AC-BDB-031)。 親 OFF の間は子の選択状態を保ったままチャートからだけ落とす —
  // 状態そのものを書き換えると、 個別に消していた子が親を OFF→ON しただけで復活
  // する。 FilterCard には生の seriesVisibility を渡し、 表示上の OFF は
  // ToggleSwitch の isDisabled が担う。
  const chartSeriesVisibility = useMemo(() => {
    const effective = { ...filter.seriesVisibility }
    for (const key of VOLTAGE_SERIES_KEYS) {
      effective[key] = filter.isVoltageEnabled && effective[key]
    }
    for (const key of ANALYSIS_SERIES_KEYS) {
      effective[key] = filter.isAnalysisEnabled && effective[key]
    }
    for (const key of DETAIL_SERIES_KEYS) {
      effective[key] = filter.isDetailEnabled && effective[key]
    }
    return effective
  }, [
    filter.seriesVisibility,
    filter.isVoltageEnabled,
    filter.isAnalysisEnabled,
    filter.isDetailEnabled,
  ])

  // workflow-back-bar-readonly AC-09/AC-13: caseId は URL クエリパラメータ
  // `?workflowId=` から取得し、詳細リンク (AreaCodeChart) へ
  // 引き継ぐ。
  const { caseId } = useCurrentWorkflowCase()

  // doji-grid-layout: レイアウト hook をページ側で持つ (= AreaCodeGrid が
  // isLoading 中は unmount されているため、hook を Grid 側に置くと「読み込み中に
  // レイアウトリセットを押しても、次のマウント時に効果が握り潰される」バグが
  // 起きる。hook をページに常駐させ、resetLayout を直接 FilterCard へ渡す)。
  const visibleAreaCodes = useMemo(
    () =>
      selectVisibleAreaCodes(
        dashboard?.areaCodes ?? [],
        filter.visibleAreaCodes,
      ),
    [dashboard, filter.visibleAreaCodes],
  )
  // 凡例の見本は表示中のエリアのどれかに低圧未着のコマがある時だけ出す
  // (design `js/bg-data.js` の renderLvPendingLegend)。
  const hasAwaitingVoltageKoma = visibleAreaCodes.some((item) =>
    hasAwaitingKoma(item.komaData),
  )
  const cardIds = useMemo(
    () => visibleAreaCodes.map((item) => areaCodeStorageKey(item.areaCode)),
    [visibleAreaCodes],
  )
  const { layout, onLayoutSettled, resetLayout } = useGridCardLayout({
    storageKey: LAYOUT_STORAGE_KEY,
    cardIds,
    defaultSize: DEFAULT_CARD_SIZE,
    minSize: MIN_CARD_SIZE,
  })

  const breadcrumbItems = [
    { label: t('common:nav.balance') },
    { label: t('page:balanceDemand.title'), bold: true },
  ]

  return (
    <WorkflowPageLayout
      breadcrumbItems={breadcrumbItems}
      // design `balance/bg.html` の `.bg-data-source` 行 (気象データ出典・
      // 最終更新) を `data-page-header-extras` で page-header 右端へ寄せる。
      headerExtra={
        <Flex alignItems="center" gap={2}>
          <WeatherDataSource />
          <Text textStyle="caption" color="Text.midEmphasis" opacity={0.6}>
            {t('common:metaSeparator')}
          </Text>
          <TakusoLastUpdated
            label={t('page:balanceDemand.takusoLastUpdated')}
            lastUpdatedAt={dashboard?.takusoLastUpdatedAt}
          />
        </Flex>
      }
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <FilterCard
          filter={filter}
          hasAwaitingVoltageKoma={hasAwaitingVoltageKoma}
        />

        {/* doji-grid-layout AC-BDB-006:「レイアウトリセット」。 2026-09-07 の
            デザイナー判断で、フィルタカードの中 (8080 の `#bg-layout-reset-btn`
            の位置) から**カードの外・グラフの上の右端**へ移した。 カードの中の
            「クリア」が絞り込みを、こちらがカードの配置を戻す、と操作の対象を
            置き場所で分けるため。 */}
        <Flex justifyContent="flex-end" marginBottom="4px">
          <TextButton onClick={resetLayout} size="sm">
            {t('page:balanceDemand.filter.layoutReset')}
          </TextButton>
        </Flex>

        {/* 取得失敗のバナー。 **レイアウトリセットの下 = 本来グラフが出る領域**に置く
            (2026-09-09 デザイナー指定「alert が指しているデータはグラフであるため」)。

            [spec 差異] AC-BDB-013 の文言は「ダッシュボード**上部**にエラーバナー」 だが、
            上部 (フィルタ直下) だと**何が取れなかったのか**が結び付かない。 バナーが
            指しているのはエリアカード群のデータなので、 その器の中に出す。 spec 側の
            追随は #7064 で扱う。

            AC は初回取得に限定していないので、 背景 refetch の失敗でもバナーを出す。
            その場合は**カードを残したままバナーを上に足す**。

            初回取得に失敗した時は**ローディングを出さない**。 以前は
            `isLoading || !dashboard` だけで分岐しており、 error でも `dashboard` が
            undefined のまま = **永久にローディング表示**になっていた (issue #7064)。 */}
        {showLoadError && (
          <Box marginBottom="4px">
            <CompactAlert
              status="error"
              title={t('page:balanceDemand.loadError')}
            />
          </Box>
        )}

        {initialLoadFailed ? null : (
          <RegionLoading
            flexDirection="column"
            flex="1"
            display="flex"
            minHeight={dashboard === undefined ? '200px' : 0}
            isLoading={isRegionLoading}
          >
            {dashboard === undefined ? null : (
              <AreaCodeGrid
                areaCodes={dashboard.areaCodes}
                caseId={caseId}
                currentKoma={dashboard.currentKoma}
                displayDate={filter.displayDate}
                gcKoma={dashboard.gcKoma}
                layout={layout}
                onLayoutSettled={onLayoutSettled}
                selectableAreaCodes={filter.selectableAreaCodes}
                seriesVisibility={chartSeriesVisibility}
                visibleAreaCodes={filter.visibleAreaCodes}
              />
            )}
          </RegionLoading>
        )}
      </div>
    </WorkflowPageLayout>
  )
}
